import {useEffect, useMemo, useState} from 'react'
import {
  IconArrowBackUp, IconChevronDown, IconChevronRight, IconEye, IconLogin, IconRefresh,
} from '@tabler/icons-react'
import {apiGet, currentUser} from './api/client'
import {diffFields, objTitle} from './api/diff'
import {
  classLabel, descLabel, history, historyOne, isPlumbing, KIND_RU, logins, undo, userLabel,
  viewsLog,
  type Change, type Login, type Txn, type View,
} from './api/history'
import {fullName, placePath, shortName} from './api/tree'
import type {TreeIndex} from './api/types'

interface Props {
  ix: TreeIndex
  onChanged: () => void
  onOpenPerson: (h: string) => void
}

/* API отдаёт СЕКУНДЫ: в базе журнала наносекунды, но сервер делит их
   на 1e9 при выдаче. Я принял их за наносекунды и делил ещё раз — все
   правки показывались как 01.01.1970. */
function when(sec: number): string {
  const d = new Date(sec * 1000)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`
}

/** Разбор одной правки по полям: что было и что стало. */
function Detail({state, ix}: {state: Txn | 'busy' | 'fail' | undefined; ix: TreeIndex}) {
  if (state === 'busy' || !state) return <div className="hdetail muted">Смотрим…</div>
  if (state === 'fail') return <div className="hdetail muted">Подробности не пришли</div>

  const parts = (state.changes || []).filter(c => !isPlumbing(c)).map(c => ({
    c,
    title: objTitle(c.obj_class, c.new_data || c.old_data || null, ix),
    fields: diffFields(c.obj_class, c.old_data || null, c.new_data || null, ix),
  }))

  if (!parts.some(p => p.fields.length)) {
    return (
      <div className="hdetail muted">
        Изменилось только служебное поле — на содержании это не сказалось.
      </div>
    )
  }

  return (
    <div className="hdetail">
      {parts.filter(p => p.fields.length).map((p, i) => (
        <div className="hpart" key={i}>
          <div className="hpart-h">
            {classLabel(p.c)} {KIND_RU[p.c.trans_type] ?? ''}
            {p.title && <b>{p.title}</b>}
          </div>
          <table className="hfields">
            <tbody>
              {p.fields.map((f, k) => (
                <tr key={k}>
                  <td className="hf-l">{f.label}</td>
                  <td className="hf-a">{f.was || <i>пусто</i>}</td>
                  <td className="hf-b">{f.now || <i>пусто</i>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  )
}

type Tab = 'edits' | 'logins' | 'views'

/** Кто чьи карточки открывал. */
function Views({ix, onTab, onOpenPerson}: {
  ix: TreeIndex
  onTab: (t: Tab) => void
  onOpenPerson: (h: string) => void
}) {
  const [rows, setRows] = useState<View[] | null>(null)
  const [err, setErr] = useState('')
  const [hideMe, setHideMe] = useState(false)
  const [reload, setReload] = useState(0)
  const me = currentUser()

  useEffect(() => {
    let alive = true
    viewsLog(500)
      .then(r => alive && setRows(r))
      .catch(e => alive && setErr(e instanceof Error ? e.message : 'Не удалось загрузить'))
    return () => { alive = false }
  }, [reload])

  const shown = (rows || []).filter(r => !hideMe || r.username !== me)

  return (
    <div className="page">
      <div className="pagehead">
        <h1>Журнал</h1>
        <Tabs tab="views" onTab={onTab} />
        <div className="muted">{rows ? `последние ${shown.length}` : ''}</div>
        <div className="spacer" />
        <button className={'btn' + (hideMe ? ' on' : '')} onClick={() => setHideMe(v => !v)}>
          Без меня
        </button>
        <button className="btn" onClick={() => setReload(n => n + 1)}>
          <IconRefresh size={15} stroke={1.7} /> Обновить
        </button>
      </div>

      <span className="hint" style={{display: 'block', marginBottom: 14}}>
        Считается на сервере, по обращениям к карточке, поэтому учитывается и
        штатный интерфейс, и наш. Повтор одной карточки в пределах получаса за
        новый просмотр не идёт: вкладку перечитали, правку сохранили, вернулись
        назад — это один интерес, а не десять. Хранится 90 дней, вдвое меньше
        входов: вход — вопрос доступа, а просмотр — то, чем человек
        интересовался. Родне стоит про этот журнал сказать.
      </span>

      {err && <div className="err inline">{err}</div>}
      {!rows && !err && <div className="muted">Загружаем…</div>}

      {rows && (
        <div className="hlist">
          {shown.map((r, i) => {
            const p = ix.people.get(r.person)
            return (
              <div className="hrow" key={i}>
                <span className="hwhen">{when(r.ts)}</span>
                <span className="hwho">{r.username}</span>
                <span className="hwhat">
                  <span className="hobj">
                    {p ? (
                      <button type="button" className="lnk"
                              onClick={() => onOpenPerson(r.person)}>{fullName(p)}</button>
                    ) : <b>карточка удалена</b>}
                  </span>
                </span>
              </div>
            )
          })}
          {!shown.length && <div className="empty">Пока пусто</div>}
        </div>
      )}
    </div>
  )
}

/* Раньше «Входы» были кнопкой в углу страницы правок — и их не нашли.
   Две вкладки в заголовке видно сразу, и понятно, что журналов два. */
function Tabs({tab, onTab}: {tab: Tab; onTab: (t: Tab) => void}) {
  return (
    <div className="jtabs">
      <button className={'chip' + (tab === 'edits' ? ' on' : '')} onClick={() => onTab('edits')}>
        Правки
      </button>
      <button className={'chip' + (tab === 'logins' ? ' on' : '')} onClick={() => onTab('logins')}>
        <IconLogin size={14} stroke={1.7} /> Входы
      </button>
      <button className={'chip' + (tab === 'views' ? ' on' : '')} onClick={() => onTab('views')}>
        <IconEye size={14} stroke={1.7} /> Просмотры
      </button>
    </div>
  )
}

/** Кто и когда заходил. Штатный Gramps этого не пишет — ведёт наш слой
 *  поверх образа, поэтому у старых записей истории тут не будет. */
function Logins({onTab}: {onTab: (t: Tab) => void}) {
  const [rows, setRows] = useState<Login[] | null>(null)
  const [err, setErr] = useState('')
  const [onlyBad, setOnlyBad] = useState(false)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    let alive = true
    logins(300)
      .then(r => alive && setRows(r))
      .catch(e => alive && setErr(e instanceof Error ? e.message : 'Не удалось загрузить'))
    return () => { alive = false }
  }, [reload])

  const shown = (rows || []).filter(r => !onlyBad || !r.ok)
  const bad = (rows || []).filter(r => !r.ok).length

  return (
    <div className="page">
      <div className="pagehead">
        <h1>Журнал</h1>
        <Tabs tab="logins" onTab={onTab} />
        <div className="muted">{rows ? `последние ${rows.length}` : ''}</div>
        <div className="spacer" />
        {bad > 0 && (
          <button className={'btn' + (onlyBad ? ' on' : '')} onClick={() => setOnlyBad(v => !v)}>
            Только неудачные <em>{bad}</em>
          </button>
        )}
        <button className="btn" onClick={() => setReload(n => n + 1)}>
          <IconRefresh size={15} stroke={1.7} /> Обновить
        </button>
      </div>

      <span className="hint" style={{display: 'block', marginBottom: 14}}>
        Записываются и удачные входы, и неудачные — подбор пароля виден только
        по вторым. Пароли и выданные токены в журнал не попадают никогда.
        Записи старше полугода удаляются: адреса родни хранить вечно незачем.
        Пометка <b>«из логов»</b> — запись добрана из журнала прокси за те дни,
        когда своего журнала ещё не было. Имени в нём нет: логин лежит в теле
        запроса рядом с паролем, а тела прокси не пишет. Где имя у такой записи
        всё же стоит — его проставил владелец, опознав свой браузер и адрес;
        пометка остаётся, чтобы восстановленное не путали с записанным.
      </span>

      {err && <div className="err inline">{err}</div>}
      {!rows && !err && <div className="muted">Загружаем…</div>}

      {rows && (
        <div className="hlist">
          {shown.map((r, i) => (
            <div className="hrow" key={i}>
              <span className="hwhen">{when(r.ts)}</span>
              <span className={'hwho' + (r.username ? '' : ' unknown')}>
                {r.username || 'имя не записано'}
              </span>
              <span className="hwhat">
                <span className="hdesc">
                  {r.ok ? 'вход' : 'неверный пароль'}
                  {!r.ok && <em className="undone">отказ</em>}
                  {r.src === 'proxy' && (
                    <em className="fromlog"
                        title={r.username
                          ? 'Запись из логов прокси; имя проставлено владельцем по браузеру и адресу'
                          : 'Запись из логов прокси: имени в них нет'}>
                      из логов
                    </em>
                  )}
                </span>
                <span className="hobj"><em>{[r.ip, r.agent].filter(Boolean).join(' · ')}</em></span>
              </span>
            </div>
          ))}
          {!shown.length && <div className="empty">Пока пусто</div>}
        </div>
      )}
    </div>
  )
}

export default function HistoryList({ix, onChanged, onOpenPerson}: Props) {
  const [tab, setTab] = useState<Tab>('edits')
  const [rows, setRows] = useState<Txn[] | null>(null)
  const [media, setMedia] = useState<Map<string, string>>(new Map())
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(0)
  const [asking, setAsking] = useState<number | null>(null)
  const [reload, setReload] = useState(0)
  /* Развёрнутая правка. Снимки «до» и «после» тянем по клику, а не вместе
     со списком: в общем списке их нет намеренно — это сотни килобайт на
     страницу, которые почти всегда не понадобятся. */
  const [open, setOpen] = useState<number | null>(null)
  const [detail, setDetail] = useState<Map<number, Txn | 'busy' | 'fail'>>(new Map())

  function toggle(id: number) {
    setOpen(cur => (cur === id ? null : id))
    if (detail.has(id)) return
    setDetail(m => new Map(m).set(id, 'busy'))
    historyOne(id)
      .then(t => setDetail(m => new Map(m).set(id, t)))
      .catch(() => setDetail(m => new Map(m).set(id, 'fail')))
  }

  useEffect(() => {
    let alive = true
    history(1, 60)
      .then(t => alive && setRows(t))
      .catch(e => alive && setErr(e instanceof Error ? e.message : 'Не удалось загрузить журнал'))
    // подписи снимков: в индексе древа их нет, а правок по медиа много
    apiGet<{handle: string; desc?: string}[]>('/media/', {pagesize: 20000})
      .then(m => alive && setMedia(new Map(m.map(x => [x.handle, x.desc || '']))))
      .catch(() => {})
    return () => { alive = false }
  }, [reload])

  /**
   * Кого правили. Без этого журнал читался как «человек — изменено»
   * и не отвечал на единственный важный вопрос: кого именно.
   */
  const nameOf = useMemo(() => (c: Change): {text: string; person?: string} => {
    const h = c.obj_handle
    const p = ix.people.get(h)
    if (p) return {text: fullName(p), person: h}
    const f = ix.families.get(h)
    if (f) {
      const pair = [f.father_handle, f.mother_handle]
        .filter((x): x is string => !!x)
        .map(x => shortName(ix.people.get(x)))
      return {text: pair.join(' + ') || 'семья'}
    }
    if (ix.places.has(h)) return {text: placePath(ix, h)}
    const m = media.get(h)
    if (m) return {text: m}
    const e = ix.events.get(h)
    if (e) return {text: typeof e.type === 'string' ? e.type : 'событие'}
    return {text: ''}
  }, [ix, media])

  async function revert(id: number) {
    setBusy(id)
    setErr('')
    try {
      await undo(id)
      setAsking(null)
      setReload(n => n + 1)
      onChanged()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не удалось отменить правку')
    } finally {
      setBusy(0)
    }
  }

  if (tab === 'logins') return <Logins onTab={setTab} />
  if (tab === 'views') return <Views ix={ix} onTab={setTab} onOpenPerson={onOpenPerson} />

  if (err && !rows) return <div className="page"><div className="err inline">{err}</div></div>
  if (!rows) return <div className="page"><div className="muted">Загружаем…</div></div>

  return (
    <div className="page">
      <div className="pagehead">
        <h1>Журнал</h1>
        <Tabs tab="edits" onTab={setTab} />
        <div className="muted">последние {rows.length}</div>
        <div className="spacer" />
        <button className="btn" onClick={() => setReload(n => n + 1)}>
          <IconRefresh size={15} stroke={1.7} /> Обновить
        </button>
      </div>

      <span className="hint" style={{display: 'block', marginBottom: 14}}>
        Видно всё, что меняли через интерфейс, и кто именно. Отмена возвращает одну
        правку — ту, на которой нажали. Если после неё эту же запись успели поправить
        снова, сервер откажется отменять: он не станет затирать чужую работу.
      </span>

      {err && <div className="err inline" style={{marginBottom: 12}}>{err}</div>}

      <div className="hlist">
        {rows.map(t => {
          const changes = (t.changes || []).filter(c => !isPlumbing(c))
          return (
            <div className="hitem" key={t.id}>
            <div className="hrow">
              <button className="icobtn flat" title="Что именно менялось"
                      onClick={() => toggle(t.id)}>
                {open === t.id
                  ? <IconChevronDown size={15} stroke={2} />
                  : <IconChevronRight size={15} stroke={2} />}
              </button>
              <span className="hwhen">{when(t.timestamp)}</span>
              <span className="hwho">{userLabel(t)}</span>
              <span className="hwhat">
                <span className="hdesc">
                  {descLabel(t)}
                  {t.undo && <em className="undone">откат</em>}
                </span>
                {changes.map((c, i) => {
                  const n = nameOf(c)
                  return (
                    <span className="hobj" key={i}>
                      {n.person ? (
                        <button type="button" className="lnk"
                                onClick={() => onOpenPerson(n.person!)}>{n.text}</button>
                      ) : (
                        <b>{n.text || classLabel(c)}</b>
                      )}
                      <em>{classLabel(c)} {KIND_RU[c.trans_type] ?? ''}</em>
                    </span>
                  )
                })}
                {!changes.length && <span className="hobj"><em>служебная запись</em></span>}
              </span>
              {asking === t.id ? (
                <span className="hask">
                  <button className="btn" onClick={() => setAsking(null)}>Нет</button>
                  <button className="btn warn" disabled={busy === t.id} onClick={() => revert(t.id)}>
                    {busy === t.id ? 'Отменяем…' : 'Отменить'}
                  </button>
                </span>
              ) : (
                <button className="icobtn" title="Отменить эту правку"
                        onClick={() => setAsking(t.id)}>
                  <IconArrowBackUp size={16} stroke={1.7} />
                </button>
              )}
            </div>
            {open === t.id && <Detail state={detail.get(t.id)} ix={ix} />}
            </div>
          )
        })}
        {!rows.length && <div className="empty">Правок пока не было</div>}
      </div>
    </div>
  )
}
